import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, ReferenceLine, Area, AreaChart, Legend,
} from "recharts";
import { TrendingUp, TrendingDown, Loader2, BookOpen, ThumbsUp, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * LearningMetricsChart — CAATF Learning Layer v1.0, Session 3
 *
 * Renders a 30-day trend of the flywheel:
 *   • Thumbs-up rate (24h window) — is the AI drifting?
 *   • KB entries active — is the knowledge base growing?
 *   • Feedback backlog — are we falling behind on reviews?
 *
 * Behind admin+2FA on the backend.  Used only on /admin/learning-queue.
 */
const KPI_TILE_CLS =
  "flex-1 min-w-[140px] p-3 rounded-lg bg-white border border-brand";

function fmtPct(v) {
  if (v === null || v === undefined) return "—";
  return `${Math.round(v * 100)}%`;
}

function DeltaBadge({ value, positiveGood = true, suffix = "" }) {
  if (value === 0 || value === null || value === undefined) {
    return <span className="text-[10px] text-brand-muted">no change</span>;
  }
  const isPositive = value > 0;
  const isGood = positiveGood ? isPositive : !isPositive;
  const Icon = isPositive ? TrendingUp : TrendingDown;
  const cls = isGood ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                     : "text-red-700 bg-red-50 border-red-200";
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-semibold ${cls}`}
    >
      <Icon className="w-3 h-3" />
      {isPositive ? "+" : ""}
      {value}
      {suffix}
    </span>
  );
}

export default function LearningMetricsChart({ testid = "learning-metrics" }) {
  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [latest, setLatest] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/learning/metrics/trend?days=30");
      // Shape the series for recharts — force numeric values, coerce nulls.
      const shaped = (data.series || []).map(d => ({
        date: d.date?.slice(5) || "",       // MM-DD
        thumbs_up_rate: d.thumbs_up_rate_24h !== null && d.thumbs_up_rate_24h !== undefined
          ? Math.round(d.thumbs_up_rate_24h * 100)
          : null,
        kb_active: d.kb_active_total || 0,
        kb_verified: d.kb_verified || 0,
        kb_provisional: d.kb_provisional || 0,
        backlog: d.feedback_backlog_new || 0,
        fb_total: d.feedback_24h_total || 0,
      }));
      setSeries(shaped);
      setSummary(data.summary || null);
      setLatest(data.latest || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load learning metrics");
    } finally {
      setLoading(false);
    }
  }

  async function forceSnapshot() {
    try {
      await api.post("/learning/metrics/snapshot");
      toast.success("Snapshot recorded");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Snapshot failed");
    }
  }

  if (loading) {
    return (
      <div
        data-testid={`${testid}-loading`}
        className="flex items-center gap-2 text-sm text-brand-muted p-6 border border-brand rounded-lg bg-white"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading 30-day trend…
      </div>
    );
  }

  const hasRates = series.some(s => s.thumbs_up_rate !== null);

  return (
    <div className="mb-5" data-testid={testid}>
      {/* KPI row */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid={`${testid}-kpis`}>
        <div className={KPI_TILE_CLS} data-testid={`${testid}-kpi-thumbs`}>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-brand-muted">
            <ThumbsUp className="w-3 h-3" /> 30-day avg thumbs-up
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <div className="text-2xl font-bold text-brand-primary">
              {fmtPct(summary?.avg_thumbs_up_rate)}
            </div>
            <div className="text-[11px] text-brand-muted">
              latest: {fmtPct(latest?.thumbs_up_rate_24h)}
            </div>
          </div>
        </div>
        <div className={KPI_TILE_CLS} data-testid={`${testid}-kpi-kb`}>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-brand-muted">
            <BookOpen className="w-3 h-3" /> KB entries (active)
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <div className="text-2xl font-bold text-brand-primary">
              {latest?.kb_active_total ?? 0}
            </div>
            <DeltaBadge value={summary?.kb_growth} positiveGood={true} />
          </div>
          <div className="text-[10px] text-brand-muted mt-0.5">
            {latest?.kb_verified ?? 0} verified · {latest?.kb_provisional ?? 0} provisional
          </div>
        </div>
        <div className={KPI_TILE_CLS} data-testid={`${testid}-kpi-backlog`}>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-brand-muted">
            <Inbox className="w-3 h-3" /> Review backlog
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <div className="text-2xl font-bold text-brand-primary">
              {latest?.feedback_backlog_new ?? 0}
            </div>
            <DeltaBadge value={summary?.backlog_delta} positiveGood={false} />
          </div>
          <div className="text-[10px] text-brand-muted mt-0.5">
            pending physician corrections
          </div>
        </div>
        <button
          onClick={forceSnapshot}
          className="text-[10px] px-2.5 py-1 rounded-md border border-brand text-brand-muted hover:bg-brand-bg self-start"
          data-testid={`${testid}-force-snapshot`}
          title="Take a snapshot right now (idempotent per calendar day)"
        >
          Snapshot now
        </button>
      </div>

      {/* Chart grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Thumbs-up trend */}
        <div className="border border-brand rounded-lg bg-white p-3" data-testid={`${testid}-chart-thumbs`}>
          <div className="text-xs font-semibold text-brand-text mb-2 flex items-center gap-1.5">
            <ThumbsUp className="w-3.5 h-3.5 text-emerald-600" />
            Thumbs-up rate (24h rolling) · last 30 days
          </div>
          <div className="h-[180px]">
            {hasRates ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} unit="%" />
                  <Tooltip
                    formatter={(v) => v === null ? "no data" : `${v}%`}
                    contentStyle={{ fontSize: 11 }}
                  />
                  <ReferenceLine y={80} stroke="#10b981" strokeDasharray="2 2" label={{ value: "target 80%", fontSize: 9, fill: "#10b981" }} />
                  <Line
                    type="monotone"
                    dataKey="thumbs_up_rate"
                    stroke="#4A0E8F"
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    connectNulls
                    name="Thumbs-up %"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-brand-muted italic">
                No rated feedback yet — physicians must hit 👍/👎 to populate this line.
              </div>
            )}
          </div>
        </div>

        {/* KB growth (stacked area) */}
        <div className="border border-brand rounded-lg bg-white p-3" data-testid={`${testid}-chart-kb`}>
          <div className="text-xs font-semibold text-brand-text mb-2 flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5 text-violet-600" />
            Knowledge base growth · verified vs provisional
          </div>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Area
                  type="monotone"
                  dataKey="kb_verified"
                  stackId="1"
                  stroke="#4A0E8F"
                  fill="#4A0E8F"
                  fillOpacity={0.7}
                  name="Verified"
                />
                <Area
                  type="monotone"
                  dataKey="kb_provisional"
                  stackId="1"
                  stroke="#9B59B6"
                  fill="#9B59B6"
                  fillOpacity={0.5}
                  name="Provisional"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
